// BodyImageGrid.js - Grid component for displaying selectable body images in Swingies app
import React, { useMemo } from "react";
import { reorderImages } from "./BodyUtils";
import "./SwingieBodyGallery";

// Layout constants (in vh units)
const HEADER_HEIGHT_VH = 12; // Title area
const BUTTONS_HEIGHT_VH = 15; // Fixed buttons at bottom + padding
const GAP_VH = 1.2; // Gap between rows
const IMAGES_PER_ROW = 4; // Approximate images per row on portrait tablets
const MIN_IMAGE_HEIGHT_VH = 18;
const MAX_IMAGE_HEIGHT_VH = 35;

// Calculate optimal image height based on image count
// Tries to fit all images in the visible area above the buttons
// Returns a vh value between MIN and MAX
const calculateImageHeight = (imageCount) => {
  if (imageCount <= 0) return MIN_IMAGE_HEIGHT_VH;

  // Available height for images (excluding header, buttons, and padding)
  const availableVh = 100 - HEADER_HEIGHT_VH - BUTTONS_HEIGHT_VH;

  // Calculate rows needed
  const rowCount = Math.ceil(imageCount / IMAGES_PER_ROW);

  // Calculate total gap space
  const totalGapVh = rowCount > 1 ? (rowCount - 1) * GAP_VH : 0;

  // Calculate optimal height to fit all rows in available space
  const optimalHeight = (availableVh - totalGapVh) / rowCount;

  // Clamp between min and max
  return Math.min(Math.max(optimalHeight, MIN_IMAGE_HEIGHT_VH), MAX_IMAGE_HEIGHT_VH);
};

const ImageGrid = ({
  images,
  selectedImages,
  toggleImageSelection,
  totalValue,
  number,
  serverUrl,
}) => {
  const sortedImages = reorderImages(images, number);

  // Calculate dynamic image height based on count
  const imageHeight = useMemo(() => {
    return calculateImageHeight(sortedImages.length);
  }, [sortedImages.length]);

  return (
    <div className="swingie-bodygallery-grid">
      {sortedImages.map((imageObj, index) => {
        const { path, url } = imageObj;
        const imageValue = parseInt(path.split("-").pop().split(".")[0][0], 10);
        const isSelected = selectedImages.includes(path);
        const isSelectable = totalValue + imageValue <= number || isSelected;

        return (
          <div
            key={path}
            className="swingie-bodygallery-image-container"
            style={{
              opacity: !isSelectable && !isSelected ? 0.5 : 1,
            }}
            onClick={() => toggleImageSelection(path)}
          >
            <img
              src={url || `${serverUrl}/image${path}?size=thumbnail`}
              alt={`Body ${index}`}
              className={`swingie-bodygallery-image ${
                isSelected ? "swingie-bodygallery-image-selected" : ""
              }`}
              style={{ height: `${imageHeight}vh` }}
            />

            {isSelected && (
              <div className="swingie-bodygallery-image-overlay">
                <span>✔</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ImageGrid;
